import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { filter, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthorizedApiService, CanAccessDirective } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import {EmployeeWithLaborConfigResponse } from '../../organization/employees/employee.interface';
import { AttendanceEventRecord } from '../attendance-event/attendance-event.interface';
import {
  AttendanceResultRecalculatePayload,
  AttendanceResultRecord,
} from './attendance-result.interface';

interface CompaniesResponse {
  data?: CompaniesRecord[];
  items?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

interface OrgUnitRecord {
  orgUnitId: string;
  companiesRecordId: string;
  name: string;
}

interface OrgUnitsResponse {
  data?: OrgUnitRecord[];
  items?: OrgUnitRecord[];
  results?: OrgUnitRecord[];
  orgUnits?: OrgUnitRecord[];
}

@Component({
  selector: 'app-attendance-result-detail',
  standalone: true,
  imports: [
    CommonModule,
    BreadcrumbComponent,
    LoadingStateComponent,
    CanAccessDirective,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    TranslateModule,
  ],
  templateUrl: './attendance-result-detail.component.html',
  styleUrl: './attendance-result-detail.component.scss',
})
export class AttendanceResultDetailComponent implements OnInit {
  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  isLoading = true;
  isRecalculating = false;
  isLoadingRelatedEvents = false;
  selectedAttendanceResultId: string | null = null;
  selectedAttendanceResult: AttendanceResultRecord | null = null;
  relatedEvents: AttendanceEventRecord[] = [];

  readonly readAccess = { resource: 'attendanceResults', action: 'read' } as const;
  readonly updateAccess = { resource: 'attendanceResults', action: 'update' } as const;

  readonly flagOptions: Array<{ key: keyof AttendanceResultRecord['flags']; titleKey: string; clearLabel: string }> = [
    { key: 'hasDelay', titleKey: 'attendanceResultsCatalog.flags.hasDelay', clearLabel: 'Sin retardo' },
    { key: 'hasEarlyExit', titleKey: 'attendanceResultsCatalog.flags.hasEarlyExit', clearLabel: 'Sin salida anticipada' },
    { key: 'hasOvertime', titleKey: 'attendanceResultsCatalog.flags.hasOvertime', clearLabel: 'Sin tiempo extra' },
    { key: 'workedOnHoliday', titleKey: 'attendanceResultsCatalog.flags.workedOnHoliday', clearLabel: 'No es día festivo' },
    { key: 'hasMissingPunch', titleKey: 'attendanceResultsCatalog.flags.hasMissingPunch', clearLabel: 'Marcajes completos' },
    { key: 'hasIncident', titleKey: 'attendanceResultsCatalog.flags.hasIncident', clearLabel: 'Sin otras incidencias' },
  ];

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.syncWithRoute();
      });

    void this.syncWithRoute();
  }

  get detailTitle(): string {
    return (
      this.selectedAttendanceResult?.employeeLabel ||
      this.selectedAttendanceResult?.employeeNumber ||
      this.t('attendanceResultsCatalog.detail.title')
    );
  }

  get detailDescription(): string {
    return this.selectedAttendanceResult
      ? this.t('attendanceResultsCatalog.detail.descriptionWithDate', { date: this.selectedAttendanceResult.workDateLabel })
      : this.t('attendanceResultsCatalog.detail.description');
  }

  get hasRelatedEvents(): boolean {
    return this.relatedEvents.length > 0;
  }

  get entryEvent(): AttendanceEventRecord | null {
    return this.relatedEvents.find((event) => this.isEventType(event, 'entry')) || null;
  }

  get exitEvent(): AttendanceEventRecord | null {
    return this.relatedEvents.find((event) => this.isEventType(event, 'exit')) || null;
  }

  get firstRecordedEvent(): AttendanceEventRecord | null {
    return this.sortedRelatedEvents[0] || null;
  }

  get lastRecordedEvent(): AttendanceEventRecord | null {
    const events = this.sortedRelatedEvents;
    return events[events.length - 1] || null;
  }

  get hasMissingEntry(): boolean {
    return !!this.selectedAttendanceResult?.flags.hasMissingPunch && !this.entryEvent && !!this.exitEvent;
  }

  get hasMissingExit(): boolean {
    return !!this.selectedAttendanceResult?.flags.hasMissingPunch && !!this.entryEvent && !this.exitEvent;
  }

  get resultHeadline(): string {
    return this.selectedAttendanceResult?.statusLabel || '-';
  }

  get resultDescription(): string {
    const result = this.selectedAttendanceResult;
    if (!result) return '';
    if (this.hasMissingEntry) return 'Falta un marcaje de entrada para completar la jornada.';
    if (this.hasMissingExit) return 'Falta un marcaje de salida para completar la jornada.';
    if (result.flags.hasMissingPunch) return 'Falta un marcaje para completar correctamente la jornada.';
    if (result.flags.hasDelay) return 'Se detectó un retardo respecto al horario programado.';
    if (result.flags.hasEarlyExit) return 'Se detectó una salida anticipada.';
    if (result.flags.hasOvertime) return 'Se detectó tiempo adicional a la jornada programada.';
    if (result.status === 'present') return 'La jornada fue calculada correctamente.';
    return 'Revisa los marcajes y validaciones utilizados para calcular este resultado.';
  }

  get resultHint(): string {
    if (this.hasMissingEntry) return 'Se encontró un marcaje de salida, pero no existe un marcaje de entrada.';
    if (this.hasMissingExit) return 'Se encontró un marcaje de entrada, pero no existe un marcaje de salida.';
    return this.selectedAttendanceResult?.incidentsSummaryLabel || '';
  }

  private get sortedRelatedEvents(): AttendanceEventRecord[] {
    return [...this.relatedEvents].sort((left, right) => {
      const leftTime = new Date(left.eventAt || '').getTime();
      const rightTime = new Date(right.eventAt || '').getTime();
      return (Number.isNaN(leftTime) ? 0 : leftTime) - (Number.isNaN(rightTime) ? 0 : rightTime);
    });
  }

  get earlyExitLabel(): string {
    return this.formatMinutes(this.selectedAttendanceResult?.earlyExitMinutes);
  }

  get journeyName(): string {
    return this.getLaborContextValue(['journeyName', 'journey.name', 'journey.label']) || '-';
  }

  get dailyScheduleName(): string {
    return this.getLaborContextValue(['dailyScheduleName', 'scheduleName', 'dailySchedule.name', 'schedule.name']) || '-';
  }

  get dailyScheduleMode(): string {
    const mode = this.getLaborContextValue(['dailyScheduleMode', 'scheduleMode', 'dailySchedule.mode', 'schedule.mode']);
    if (!mode) return '-';
    const key = `dailySchedulesCatalog.modeOptions.${mode}`;
    const translated = this.t(key);
    return translated !== key ? translated : this.humanizeTechnicalValue(mode);
  }

  get calculationSourceLabel(): string {
    const source = this.selectedAttendanceResult?.calculationSource;
    if (!source) return '-';
    const knownSources: Record<string, string> = {
      attendance_events: 'Marcajes de asistencia',
      manual_recalculation: 'Recálculo manual',
      manual_override: 'Corrección manual',
      imported: 'Importación',
    };
    return knownSources[source.toLowerCase()] || this.humanizeTechnicalValue(source);
  }

  get dailyScheduleRange(): string {
    const start = this.getLaborContextValue(['startTime', 'dailySchedule.startTime', 'schedule.startTime']);
    const end = this.getLaborContextValue(['endTime', 'dailySchedule.endTime', 'schedule.endTime']);
    return start && end ? `${this.formatClockTime(start)} – ${this.formatClockTime(end)}` : '-';
  }

  get hasTechnicalDetails(): boolean {
    return !!this.selectedAttendanceResult?.rawPayload;
  }

  get primaryIncidentCode(): string {
    return this.selectedAttendanceResult?.incidentCodes[0] || (this.selectedAttendanceResult?.flags.hasMissingPunch ? 'missing_punch' : 'incident');
  }

  get primaryIncidentLabel(): string {
    const key = `attendanceResultsCatalog.incidentCodes.${this.primaryIncidentCode}`;
    const translated = this.t(key);
    if (translated && translated !== key) {
      return translated;
    }
    return this.primaryIncidentCode
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  get primaryIncidentTechnicalCode(): string {
    return this.primaryIncidentCode.replace(/[\s-]+/g, '_').toUpperCase();
  }

  eventTimeLabel(event: AttendanceEventRecord | null): string {
    return event ? this.formatTimeOnly(event.eventAt, event.timezone) : '—';
  }

  async goBackToList(): Promise<void> {
    await this.router.navigate(['/attendance/attendance-results']);
  }

  async recalculateSelected(): Promise<void> {
    if (!this.selectedAttendanceResult || !this.hasAccess('attendanceResults', 'update')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '440px',
      data: {
        title: this.t('attendanceResultsCatalog.dialog.recalculateTitle'),
        message: this.t('attendanceResultsCatalog.dialog.recalculateSingleMessage', {
          employee: this.selectedAttendanceResult.employeeNumber,
          workDate: this.selectedAttendanceResult.workDateLabel,
        }),
        confirmLabel: this.t('attendanceResultsCatalog.dialog.recalculateConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    this.isRecalculating = true;
    try {
      const payload: AttendanceResultRecalculatePayload = {
        employeeId: this.selectedAttendanceResult.employeeId,
        workDate: this.selectedAttendanceResult.workDate,
        reason: 'manual_recalculation',
      };
      await firstValueFrom(this.apiService.post('attendance-results/recalculate', payload));
      this.showNotification('snackbar-success', this.t('attendanceResultsCatalog.notifications.recalculationQueued'), 'bottom', 'center');
    } catch (error) {
      console.error('Error queueing attendance result recalculation', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceResultsCatalog.notifications.recalculationError')),
        'bottom',
        'center'
      );
    } finally {
      this.isRecalculating = false;
    }
  }

  private async syncWithRoute(): Promise<void> {
    const attendanceResultId = this.route.snapshot.paramMap.get('attendanceResultId');
    if (!attendanceResultId) {
      this.selectedAttendanceResultId = null;
      this.selectedAttendanceResult = null;
      this.isLoading = false;
      return;
    }

    await this.loadAttendanceResult(attendanceResultId);
  }

  private async loadAttendanceResult(attendanceResultId: string): Promise<void> {
    this.isLoading = true;
    try {
      const response = await firstValueFrom(this.apiService.get<AttendanceResultRecord>(`attendance-results/${attendanceResultId}`));
      this.selectedAttendanceResultId = response.attendanceResultId;
      const normalizedResult = this.normalizeAttendanceResult(response);
      this.selectedAttendanceResult = await this.enrichDisplayNames(normalizedResult);
      await this.loadRelatedEvents(normalizedResult.eventIds);
    } catch (error) {
      console.error('Error loading attendance result detail', error);
      this.relatedEvents = [];
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceResultsCatalog.notifications.loadDetailError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  private normalizeAttendanceResult(item: Partial<AttendanceResultRecord>): AttendanceResultRecord {
    const status = typeof item.status === 'string' && item.status.trim() ? item.status.trim() : 'pending';
    const isActive = item.isActive !== false;
    const isClosed = item.isClosed === true;
    const incidentCodes = Array.isArray(item.incidentCodes) ? item.incidentCodes.filter(Boolean) : [];

    return {
      attendanceResultId: item.attendanceResultId || '',
      center: item.center || '',
      employeeId: item.employeeId || '',
      employeeNumber: item.employeeNumber || '-',
      companiesRecordId: item.companiesRecordId || '',
      orgUnitId: item.orgUnitId || null,
      orgPath: item.orgPath || null,
      workDate: item.workDate || '',
      timezone: item.timezone || '',
      status,
      eventIds: Array.isArray(item.eventIds) ? item.eventIds.filter(Boolean) : [],
      firstEventAt: item.firstEventAt || null,
      lastEventAt: item.lastEventAt || null,
      workedMinutes: item.workedMinutes ?? null,
      scheduledMinutes: item.scheduledMinutes ?? null,
      delayMinutes: item.delayMinutes ?? null,
      overtimeMinutes: item.overtimeMinutes ?? null,
      flags: {
        hasDelay: item.flags?.hasDelay === true,
        hasEarlyExit: item.flags?.hasEarlyExit === true,
        hasOvertime: item.flags?.hasOvertime === true,
        workedOnHoliday: item.flags?.workedOnHoliday === true,
        hasMissingPunch: item.flags?.hasMissingPunch === true,
        hasIncident: item.flags?.hasIncident === true,
      },
      incidentCodes,
      manualOverride: item.manualOverride || null,
      calculatedAt: item.calculatedAt || null,
      calculationSource: item.calculationSource || null,
      version: item.version ?? null,
      isClosed,
      isActive,
      rawPayload: item.rawPayload || null,
      createdAt: item.createdAt || null,
      updatedAt: item.updatedAt || null,
      employeeLabel: item.employeeLabel || item.employeeNumber || item.employeeId || '-',
      companyName: item.companyName || item.companiesRecordId || '-',
      orgUnitName: item.orgUnitName || item.orgUnitId || '-',
      statusLabel: this.t(`attendanceResultsCatalog.statuses.${status}`),
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
      closedLabel: isClosed ? this.t('common.yes') : this.t('common.no'),
      workDateLabel: this.formatDate(item.workDate),
      firstEventAtLabel: this.formatDateTime(item.firstEventAt),
      lastEventAtLabel: this.formatDateTime(item.lastEventAt),
      calculatedAtLabel: this.formatDateTime(item.calculatedAt),
      workedHoursLabel: this.formatMinutes(item.workedMinutes),
      scheduledHoursLabel: this.formatMinutes(item.scheduledMinutes),
      delayLabel: this.formatMinutes(item.delayMinutes),
      overtimeLabel: this.formatMinutes(item.overtimeMinutes),
      incidentsSummaryLabel: incidentCodes.length
        ? incidentCodes.map((code) => this.t(`attendanceResultsCatalog.incidentCodes.${code}`)).join(', ')
        : this.t('attendanceResultsCatalog.labels.noIncidents'),
    };
  }

  private async enrichDisplayNames(result: AttendanceResultRecord): Promise<AttendanceResultRecord> {
    const [employeeContext, companyName, orgUnitName] = await Promise.all([
      this.loadEmployeeContext(result.employeeId),
      this.loadCompanyName(result.companiesRecordId),
      this.loadOrgUnitName(result.orgUnitId),
    ]);

    return {
      ...result,
      employeeLabel: employeeContext.name || result.employeeLabel || result.employeeId,
      companyName: companyName || result.companyName || result.companiesRecordId,
      orgUnitName: orgUnitName || employeeContext.orgUnitName || result.orgUnitName || result.orgUnitId || '-',
    };
  }

  private async loadRelatedEvents(eventIds: string[]): Promise<void> {
    if (!eventIds.length) {
      this.relatedEvents = [];
      return;
    }

    this.isLoadingRelatedEvents = true;
    try {
      const responses = await Promise.allSettled(
        eventIds.map((eventId) => firstValueFrom(this.apiService.get<AttendanceEventRecord>(`attendance/events/${eventId}`)))
      );

      this.relatedEvents = responses
        .filter((result): result is PromiseFulfilledResult<AttendanceEventRecord> => result.status === 'fulfilled')
        .map((result) => this.normalizeRelatedEvent(result.value))
        .sort((left, right) => left.eventAt.localeCompare(right.eventAt));
    } catch (error) {
      console.error('Error loading related attendance events for attendance result', error);
      this.relatedEvents = [];
    } finally {
      this.isLoadingRelatedEvents = false;
    }
  }

  private normalizeRelatedEvent(event: Partial<AttendanceEventRecord>): AttendanceEventRecord {
    const source = `${event.source || 'device'}`.toLowerCase();
    const eventType = event.eventType || '';
    const normalizedEventType = event.normalizedEventType || null;
    const status = event.status || '';
    const faceValidationStatus = event.faceValidationStatus || null;
    const isActive = event.isActive !== false;

    return {
      attendanceEventId: event.attendanceEventId || '',
      center: event.center || '',
      employeeId: event.employeeId || '',
      employeeNumber: event.employeeNumber || this.selectedAttendanceResult?.employeeNumber || '-',
      companiesRecordId: event.companiesRecordId || '',
      orgUnitId: event.orgUnitId || null,
      orgPath: event.orgPath || null,
      source,
      eventType,
      normalizedEventType,
      eventAt: event.eventAt || '',
      timezone: event.timezone || this.selectedAttendanceResult?.timezone || 'America/Cancun',
      deviceId: event.deviceId || null,
      deviceVersion: event.deviceVersion || null,
      lat: event.lat ?? null,
      lng: event.lng ?? null,
      accuracy: event.accuracy ?? null,
      faceValidationStatus,
      status,
      rawPayload: event.rawPayload || null,
      isActive,
      employeeLabel: this.selectedAttendanceResult?.employeeLabel || event.employeeNumber || '-',
      sourceLabel: this.t(`attendanceEventsCatalog.sources.${source}`),
      eventTypeLabel: eventType ? this.t(`attendanceEventsCatalog.eventTypes.${eventType}`) : '-',
      normalizedEventTypeLabel: normalizedEventType ? this.t(`attendanceEventsCatalog.eventTypes.${normalizedEventType}`) : '-',
      eventAtLabel: this.formatDateTime(event.eventAt),
      statusLabel: status ? this.t(`attendanceEventsCatalog.statuses.${status}`) : '-',
      faceValidationStatusLabel: faceValidationStatus ? this.t(`attendanceEventsCatalog.faceStatuses.${faceValidationStatus}`) : '-',
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private async loadEmployeeContext(employeeId: string): Promise<{ name: string; orgUnitName: string }> {
    if (!employeeId) {
      return { name: '', orgUnitName: '' };
    }
    try {
      const response = await firstValueFrom(
        this.apiService.get<EmployeeWithLaborConfigResponse>(`employees/${employeeId}/with-operational-config`)
      );
      return {
        name: response?.entity?.fullName || response?.entity?.employeeNumber || '',
        orgUnitName: response?.entity?.orgUnitName || '',
      };
    } catch (error) {
      console.error('Error loading employee name for attendance result', error);
      return { name: '', orgUnitName: '' };
    }
  }

  private async loadCompanyName(companiesRecordId: string): Promise<string> {
    if (!companiesRecordId) {
      return '';
    }
    try {
      const response = await firstValueFrom(
        this.apiService.get<CompaniesRecord[] | CompaniesResponse>('companies-records')
      );
      const company = this.extractCompanies(response).find((item) => item.companiesRecordId === companiesRecordId);
      return company?.name || '';
    } catch (error) {
      console.error('Error loading company name for attendance result', error);
      return '';
    }
  }

  private async loadOrgUnitName(orgUnitId?: string | null): Promise<string> {
    if (!orgUnitId) {
      return '';
    }
    try {
      const detail = await firstValueFrom(this.apiService.get<OrgUnitRecord>(`org-units/${orgUnitId}`));
      if (detail?.name) {
        return detail.name;
      }
    } catch {
      // Some deployments expose only the org-unit catalog endpoint.
    }
    try {
      const response = await firstValueFrom(this.apiService.get<OrgUnitRecord[] | OrgUnitsResponse>('org-units'));
      const orgUnit = this.extractOrgUnits(response).find((item) => item.orgUnitId === orgUnitId);
      return orgUnit?.name || '';
    } catch (error) {
      console.error('Error loading org unit name for attendance result', error);
      return '';
    }
  }

  private extractCompanies(response: CompaniesRecord[] | CompaniesResponse): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.companiesRecords || [];
  }

  private extractOrgUnits(response: OrgUnitRecord[] | OrgUnitsResponse): OrgUnitRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.orgUnits || [];
  }

  private formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }

  private formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private formatMinutes(value?: number | null): string {
    if (value === null || value === undefined) {
      return '-';
    }
    const totalMinutes = Math.max(0, Math.round(Number(value) || 0));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours} h ${String(minutes).padStart(2, '0')} min`;
  }

  formatCoordinates(event: AttendanceEventRecord): string {
    if (event.lat === null || event.lat === undefined || event.lng === null || event.lng === undefined) {
      return '-';
    }
    return `${event.lat}, ${event.lng}`;
  }

  private isEventType(event: AttendanceEventRecord, expected: 'entry' | 'exit'): boolean {
    const value = `${event.normalizedEventType || event.eventType || ''}`.toLowerCase();
    return expected === 'entry'
      ? value === 'entry' || value === 'entrada'
      : value === 'exit' || value === 'salida';
  }

  private getLaborContextValue(paths: string[]): string {
    const context = this.selectedAttendanceResult?.rawPayload?.laborContext;
    if (!context || typeof context !== 'object') return '';
    for (const path of paths) {
      let value: unknown = context;
      for (const segment of path.split('.')) {
        if (!value || typeof value !== 'object') {
          value = undefined;
          break;
        }
        value = (value as Record<string, unknown>)[segment];
      }
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  }

  private formatTimeOnly(value?: string | null, timezone?: string): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      timeZone: timezone || this.selectedAttendanceResult?.timezone || 'America/Cancun',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private formatClockTime(value: string): string {
    if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(value)) return value;
    const [hourText, minute] = value.split(':');
    const hour = Number(hourText);
    return `${(hour % 12 || 12).toString().padStart(2, '0')}:${minute} ${hour >= 12 ? 'p.m.' : 'a.m.'}`;
  }

  private humanizeTechnicalValue(value: string): string {
    return value
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .split(/[_\-\s]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  private hasAccess(resource: string, action: string): boolean {
    return this.authorizationService.hasAccess({ resource, action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
